import { type FC, memo, useCallback, useState } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Feature, RecordParticipationTypes, RemoveFeature } from "@viagogo/feature";
import { SiteErrorBoundary } from "src/shared/components/SiteErrorBoundary";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { BoundaryNames } from "src/shared/utils/sentry/BoundaryNames";
import { CityLinks } from "src/website-apps/responsive/stubhub-performer/components/city-links/CityLinks";
import { LoadingCarouselsExperiment } from "src/website-apps/responsive/stubhub-performer/components/performer-carousel-skeleton/LoadingCarouselsExperiment";
import { PerformerCarouselsV2 } from "src/website-apps/responsive/stubhub-performer/components/performer-carousels/PerformerCarouselsV2";
import { marginDivLarge } from "src/website-apps/responsive/stubhub-performer/components/styled-components";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { AppInstallBanner, AppInstallBannerPlacements } from "src/website-apps/shared/components/app-install-banner";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { LazyLoad } from "src/website-apps/shared/components/lazy-load";
import { getPerformerResidencyContext } from "src/website-apps/shared/utils/performerGridSurface";
import { SeoBottomContent } from "../../seo-bottom-content/SeoBottomContent";

const appInstallBannerTopMargin = css({ marginBlockStart: "primitives.300" });

interface Props {
  performerInfo: { hasEnoughData: boolean | null };
}

/** Section containing performer carousels, email sign up, app install banner, city links, and SEO content. */
export const PerformerCarouselSection: FC<Props> = memo(({ performerInfo }) => {
  const model = usePerformerPageModelData();
  const { categorySummary } = model;
  const appContext = useAppContext();
  const [areCarouselsLoaded, setAreCarouselsLoaded] = useState(false);
  const residencyContext = getPerformerResidencyContext(model.performerGridSurface);

  const venueLocation =
    residencyContext?.venueLatitude !== null &&
    residencyContext?.venueLatitude !== undefined &&
    residencyContext.venueLongitude !== null &&
    residencyContext.venueLongitude !== undefined &&
    residencyContext.venueCityName
      ? { lt: residencyContext.venueLatitude, lg: residencyContext.venueLongitude, n: residencyContext.venueCityName }
      : undefined;

  /** Chose 600 in order to follow logical order of page. */
  const appInstallBannerOffset = 600;
  /** Chose 700 in order to follow logical order of page. */
  const emailSignUpOffset = 700;
  /** Chose 800 in order to follow logical order of page. */
  const cityLinksOffset = 800;

  const handleCarouselsLoaded = useCallback(() => {
    setAreCarouselsLoaded(true);
  }, []);

  return (
    <>
      <>
        <Feature
          id={FeatureSetEnum.TMELEagerLazyLoad}
          recordType={RecordParticipationTypes.IN_VIEW}
          control={({ children }) => <LazyLoad>{children}</LazyLoad>}
          variant={({ children }) => (
            <Feature
              id={FeatureSetEnum.TMPerformerSuperEagerLazyLoad}
              recordType={RecordParticipationTypes.IN_VIEW}
              control={({ children }) => <LazyLoad offset={400}>{children}</LazyLoad>}
              variant={({ children }) => <LazyLoad offset={600}>{children}</LazyLoad>}
            >
              {children}
            </Feature>
          )}
        >
          <SiteErrorBoundary boundaryName={BoundaryNames.PERFORMER_CAROUSELS}>
            <PerformerCarouselsV2
              categorySummary={categorySummary}
              profileUrl={model.header.profileUrl.url}
              onLoad={handleCarouselsLoaded}
              venueLocation={venueLocation}
              isResidency={residencyContext !== null}
            />
          </SiteErrorBoundary>
        </Feature>
        <LoadingCarouselsExperiment isLoading={!areCarouselsLoaded} />
      </>
      {!appContext.isNativeApp && (
        <RemoveFeature
          id={FeatureSetEnum.MPerformerAppInstallInFeedBanner}
          feature={() => (
            <Feature
              id={FeatureSetEnum.TMPerformerLazyLoadAppDownloadEmailSignup}
              control={({ children }) => children}
              variant={({ children }) => <LazyLoad offset={appInstallBannerOffset}>{children}</LazyLoad>}
            >
              <AppInstallBanner
                className={appInstallBannerTopMargin}
                placement={AppInstallBannerPlacements.PERFORMER}
                userId={appContext.userId}
              />
            </Feature>
          )}
        />
      )}
      <SeoBottomContent performerInfo={performerInfo} />
      {!!model.cityLinks && model.cityLinks.length > 1 && (
        <LazyLoad offset={cityLinksOffset}>
          <CityLinks
            categoryName={model.categorySummary.categoryName ?? ""}
            cityLinks={model.cityLinks}
            categoryUrl={model.categorySummary.categoryUrl ?? "#"}
            totalEventCount={model.totalUnfilteredEventsCount}
            showAllLocationsButton={!!model.seoCity}
          />
        </LazyLoad>
      )}
      <div className={marginDivLarge}>
        {!appContext.isNativeApp && (
          <Feature
            id={FeatureSetEnum.TMPerformerLazyLoadAppDownloadEmailSignup}
            control={({ children }) => children}
            variant={({ children }) => <LazyLoad offset={emailSignUpOffset}>{children}</LazyLoad>}
          >
            <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
          </Feature>
        )}
      </div>
    </>
  );
});
PerformerCarouselSection.displayName = "PerformerCarouselSection";
